import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Archive, ArchiveRestore, CheckCheck, Clock3, MoreHorizontal, PanelRightOpen, Trash2, UserRound } from "lucide-react";
import { capitalizeName } from "src/utils/getInitials";
import { isRealtimeEnabled } from "src/realtime/echo";
import { Monogram, WsDialog } from "src/stage/StageUi";
import HelpTip from "src/stage/HelpTip";

// AUTO-WA-39: who you're talking to, and — in words — whether you can still
// reply with any message (the 24-hour window), counting down.
export function replyWindow(endsAt, now = Date.now()) {
  if (endsAt === undefined) return null;
  if (endsAt === null) return { open: false, tone: "closed", label: "Waiting for their first message" };
  const left = endsAt - now;
  if (left <= 0) return { open: false, tone: "closed", label: "Reply window closed" };
  const hours = Math.floor(left / 3600000);
  const minutes = Math.max(1, Math.floor((left % 3600000) / 60000));
  return { open: true, tone: left < 2 * 3600000 ? "soon" : "open", label: hours ? `Open to reply · ${hours}h ${minutes}m left` : `Open to reply · ${minutes} min left` };
}

function useMinuteTick() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

function ChatHeader({
  conversation,
  onProfileClick,
  onDeleteConversation,
  onArchive,
  onMarkRead,
  onBack,
  realtimeConnected,
  windowEndsAt,
}) {
  const contact = conversation.contact || {};
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const menuRef = useRef(null);
  const now = useMinuteTick();
  const window24 = replyWindow(windowEndsAt, now);
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const name = capitalizeName(contact?.name || contact?.phone_number || "Unknown customer");

  useEffect(() => {
    if (!showMenu) return undefined;
    const onPointer = (event) => { if (menuRef.current && !menuRef.current.contains(event.target)) setShowMenu(false); };
    const onKey = (event) => { if (event.key === "Escape") setShowMenu(false); };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onPointer); document.removeEventListener("keydown", onKey); };
  }, [showMenu]);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await onDeleteConversation?.(conversation.id);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Failed to delete conversation:", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <header className="wa-chat-head">
        <button type="button" onClick={onBack} className="ws-row-more wa-chat-back" aria-label="Back to conversations" title="Back to conversations">
          <ArrowLeft size={17} />
        </button>
        <button type="button" onClick={onProfileClick} className="wa-chat-who" aria-label={`Open details for ${name}`}>
          <Monogram name={name} size={40} />
          <span className="ws-grow" style={{ minWidth: 0 }}>
            <strong className="wa-ellipsis">{name}</strong>
            <span className="wa-chat-who-sub">
              <span>{contact?.phone_number || "—"}</span>
              {conversation.assigned_user && <span className="wa-chat-owner"><UserRound size={11} aria-hidden="true" />{conversation.assigned_user.name}</span>}
              {isRealtimeEnabled() && <span className={`wa-live${realtimeConnected ? "" : " is-off"}`} title={realtimeConnected ? "Live updates on" : "Reconnecting…"}>{realtimeConnected ? "Live" : "Reconnecting"}</span>}
            </span>
          </span>
        </button>

        {window24 && (
          <span className={`wa-window wa-window--${window24.tone}`} role="status">
            <Clock3 size={13} aria-hidden="true" />{window24.label}
            <HelpTip term="window" align="right" />
          </span>
        )}

        <div className="wa-chat-actions" ref={menuRef}>
          {Number(conversation?.unread_count || 0) > 0 && (
            <button type="button" onClick={onMarkRead} className="ws-row-more" title="Mark as read" aria-label="Mark as read"><CheckCheck size={16} /></button>
          )}
          <button type="button" onClick={onArchive} className="ws-row-more" title={conversation?.is_archived ? "Move back to Chats" : "Archive chat"} aria-label={conversation?.is_archived ? "Unarchive conversation" : "Archive conversation"}>
            {conversation?.is_archived ? <ArchiveRestore size={16} /> : <Archive size={16} />}
          </button>
          <button type="button" onClick={onProfileClick} className="ws-btn ws-btn--quiet ws-btn--sm" title="Contact details" aria-label="Contact details">
            <PanelRightOpen size={15} /><span className="wa-hide-sm">Details</span>
          </button>
          {isAdmin && (
            <span style={{ position: "relative", display: "inline-flex" }}>
              <button type="button" onClick={() => setShowMenu((open) => !open)} className="ws-row-more" aria-haspopup="menu" aria-expanded={showMenu} aria-label="More actions"><MoreHorizontal size={16} /></button>
              {showMenu && (
                <div className="ws-row-menu" role="menu">
                  <button role="menuitem" type="button" className="ws-menu-item" style={{ color: "#AD3B3B" }} onClick={() => { setShowMenu(false); setShowDeleteConfirm(true); }}>
                    <Trash2 size={15} color="#AD3B3B" />Delete chat
                  </button>
                </div>
              )}
            </span>
          )}
        </div>
      </header>
      {showDeleteConfirm && (
        <WsDialog title="Delete this chat?" description="Its messages are removed from Auto Appy. The customer keeps their copy on WhatsApp." onClose={() => !deleting && setShowDeleteConfirm(false)}>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="ws-btn ws-btn--quiet" data-close onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>Keep it</button>
            <button type="button" className="ws-btn ws-btn--danger" onClick={confirmDelete} disabled={deleting}><Trash2 size={14} />{deleting ? "Deleting…" : "Delete chat"}</button>
          </div>
        </WsDialog>
      )}
    </>
  );
}

export default ChatHeader;
